import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { useGetPayrollSummaryQuery } from '../../services/reportsApi';

const PayrollSummary = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const {
    data: summaryData,
    isLoading,
    error,
    refetch
  } = useGetPayrollSummaryQuery({
    month: selectedMonth,
    year: selectedYear,
    department: selectedDepartment
  });

  useEffect(() => {
    console.log('PayrollSummary Debug:', {
      selectedMonth,
      selectedYear,
      selectedDepartment,
      isLoading,
      error: error?.message || error,
      data: summaryData
    });
    refetch();
  }, [selectedMonth, selectedYear, selectedDepartment, refetch, isLoading, error, summaryData]);

  const handleExportSummary = () => {
    const queryParams = new URLSearchParams({
      month: selectedMonth,
      year: selectedYear,
      department: selectedDepartment,
      export: 'csv'
    });
    
    window.open(`/api/reports/payroll-summary?${queryParams}`, '_blank');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const departments = [
    { value: 'all', label: 'All Departments' },
    { value: 'Engineering', label: 'Engineering' },
    { value: 'Human Resources', label: 'Human Resources' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Sales', label: 'Sales' },
    { value: 'Operations', label: 'Operations' },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading payroll summary: {error.message}
      </Alert>
    );
  }

  // Mock data for testing - remove this once API is working
  const mockData = {
    totalGrossPay: 136000,
    totalDeductions: 9605,
    totalNetPay: 126395,
    totalEmployees: 3,
    departmentBreakdown: [
      {
        department: "Sales",
        employeeCount: 1,
        grossPay: 45333,
        deductions: 2035,
        netPay: 43298,
        avgSalary: 45333
      },
      {
        department: "Design",
        employeeCount: 1,
        grossPay: 45333,
        deductions: 3785,
        netPay: 41548,
        avgSalary: 45333
      },
      {
        department: "Quality Assurance",
        employeeCount: 1,
        grossPay: 45333,
        deductions: 3785,
        netPay: 41548,
        avgSalary: 45333
      }
    ],
    earningsBreakdown: {
      basic: 54400,
      hra: 27200,
      allowances: 54400,
      overtime: 0
    },
    deductionsBreakdown: {
      pf: 5400,
      esi: 0,
      pt: 705,
      tds: 3500,
      other: 0
    },
    monthlyComparison: {
      current: 136000,
      previous: 120000,
      change: 13.3
    }
  };

  const summary = summaryData || mockData;

  console.log('Summary Data:', summary);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 25%, #e8eaf6 100%)',
      p: 3
    }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 700,
          background: 'linear-gradient(135deg, #1e293b 0%, #3b82f6 50%, #6366f1 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 2
        }}
      >
        Payroll Summary Report
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        paragraph
        sx={{ mb: 3, fontSize: '1.1rem' }}
      >
        Monthly payroll summaries and cost analysis with department-wise breakdown
      </Typography>

      {/* Filters */}
      <Paper sx={{
        p: 3,
        mb: 3,
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        borderRadius: '24px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {monthNames.map((month, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {[2023, 2024, 2025, 2026].map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartment}
                label="Department"
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                {departments.map((dept) => (
                  <MenuItem key={dept.value} value={dept.value}>
                    {dept.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportSummary}
              fullWidth
              sx={{
                height: '56px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, opacity: 0.9 }}>
                    Gross Payroll
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {formatCurrency(summary.totalGrossPay)}
                  </Typography>
                </Box>
                <AccountBalanceIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(240, 147, 251, 0.3)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 40px rgba(240, 147, 251, 0.4)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, opacity: 0.9 }}>
                    Total Deductions
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {formatCurrency(summary.totalDeductions)}
                  </Typography>
                </Box>
                <TrendingDownIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(79, 172, 254, 0.3)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 40px rgba(79, 172, 254, 0.4)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, opacity: 0.9 }}>
                    Net Payroll
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {formatCurrency(summary.totalNetPay)}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            color: '#333',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(168, 237, 234, 0.3)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 12px 40px rgba(168, 237, 234, 0.4)'
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, opacity: 0.8 }}>
                    Total Employees
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {summary.totalEmployees}
                  </Typography>
                </Box>
                <GroupIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Comparison */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{
            p: 3,
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <Typography variant="h6" gutterBottom sx={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 600
            }}>
              Monthly Comparison
            </Typography>
            <Box display="flex" alignItems="center" gap={3} sx={{ flexWrap: 'wrap' }}>
              <Box sx={{
                p: 2,
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                minWidth: '150px'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Current Month
                </Typography>
                <Typography variant="h6" sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 600
                }}>
                  {formatCurrency(summary.monthlyComparison.current)}
                </Typography>
              </Box>
              <Box sx={{
                p: 2,
                background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(75, 85, 99, 0.1) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(107, 114, 128, 0.2)',
                minWidth: '150px'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Previous Month
                </Typography>
                <Typography variant="h6" sx={{
                  background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 600
                }}>
                  {formatCurrency(summary.monthlyComparison.previous)}
                </Typography>
              </Box>
              <Chip
                label={`${summary.monthlyComparison.change > 0 ? '+' : ''}${summary.monthlyComparison.change.toFixed(1)}%`}
                sx={{
                  background: summary.monthlyComparison.change > 0
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  height: '32px',
                  borderRadius: '16px',
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                }}
                size="small"
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{
            p: 3,
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <Typography variant="h6" gutterBottom sx={{
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 600
            }}>
              Earnings vs Deductions
            </Typography>
            <Box display="flex" justifyContent="space-between" gap={3}>
              <Box textAlign="center" sx={{
                p: 3,
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                flex: 1
              }}>
                <Typography variant="h4" sx={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 700,
                  mb: 1
                }}>
                  {summary.totalGrossPay > 0 ? ((summary.totalGrossPay - summary.totalDeductions) / summary.totalGrossPay * 100).toFixed(1) : '0.0'}%
                </Typography>
                <Typography variant="body2" color="text.secondary">Net Pay Ratio</Typography>
              </Box>
              <Box textAlign="center" sx={{
                p: 3,
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                flex: 1
              }}>
                <Typography variant="h4" sx={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 700,
                  mb: 1
                }}>
                  {summary.totalGrossPay > 0 ? (summary.totalDeductions / summary.totalGrossPay * 100).toFixed(1) : '0.0'}%
                </Typography>
                <Typography variant="body2" color="text.secondary">Deduction Ratio</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Department-wise Breakdown */}
      <Paper sx={{
        p: 3,
        mb: 3,
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        borderRadius: '24px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <Typography variant="h6" gutterBottom sx={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 600,
          mb: 3
        }}>
          Department-wise Breakdown
        </Typography>
        <TableContainer sx={{
          borderRadius: '16px',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          overflow: 'hidden'
        }}>
          <Table>
            <TableHead sx={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              '& .MuiTableCell-head': {
                fontWeight: 600,
                color: '#374151',
                borderBottom: '2px solid #e5e7eb'
              }
            }}>
              <TableRow>
                <TableCell>Department</TableCell>
                <TableCell align="right">Employees</TableCell>
                <TableCell align="right">Gross Pay</TableCell>
                <TableCell align="right">Deductions</TableCell>
                <TableCell align="right">Net Pay</TableCell>
                <TableCell align="right">Avg Salary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.departmentBreakdown.map((dept, index) => (
                <TableRow
                  key={dept.department}
                  sx={{
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.02) 0%, rgba(99, 102, 241, 0.02) 100%)',
                      transform: 'scale(1.01)',
                      transition: 'all 0.2s ease'
                    },
                    '& .MuiTableCell-body': {
                      borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                      py: 2
                    }
                  }}
                >
                  <TableCell sx={{
                    fontWeight: 600,
                    color: '#1f2937',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Box sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: `hsl(${index * 45}, 70%, 50%)`
                    }} />
                    {dept.department}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {dept.employeeCount}
                  </TableCell>
                  <TableCell align="right" sx={{
                    fontWeight: 600,
                    color: '#059669'
                  }}>
                    {formatCurrency(dept.grossPay)}
                  </TableCell>
                  <TableCell align="right" sx={{
                    fontWeight: 600,
                    color: '#dc2626'
                  }}>
                    {formatCurrency(dept.deductions)}
                  </TableCell>
                  <TableCell align="right" sx={{
                    fontWeight: 600,
                    color: '#2563eb'
                  }}>
                    {formatCurrency(dept.netPay)}
                  </TableCell>
                  <TableCell align="right" sx={{
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    {formatCurrency(dept.avgSalary)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Earnings Breakdown */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{
            p: 3,
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            height: '100%'
          }}>
            <Typography variant="h6" gutterBottom sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 600,
              mb: 3
            }}>
              Earnings Breakdown
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}>
                <Typography sx={{ fontWeight: 500 }}>Basic Salary:</Typography>
                <Typography sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {formatCurrency(summary.earningsBreakdown.basic)}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <Typography sx={{ fontWeight: 500 }}>HRA:</Typography>
                <Typography sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {formatCurrency(summary.earningsBreakdown.hra)}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(245, 158, 11, 0.2)'
              }}>
                <Typography sx={{ fontWeight: 500 }}>Allowances:</Typography>
                <Typography sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {formatCurrency(summary.earningsBreakdown.allowances)}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(139, 92, 246, 0.2)'
              }}>
                <Typography sx={{ fontWeight: 500 }}>Overtime:</Typography>
                <Typography sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {formatCurrency(summary.earningsBreakdown.overtime)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{
            p: 3,
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            height: '100%'
          }}>
            <Typography variant="h6" gutterBottom sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 600,
              mb: 3
            }}>
              Deductions Breakdown
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(239, 68, 68, 0.2)'
              }}>
                <Typography sx={{ fontWeight: 500 }}>PF:</Typography>
                <Typography sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {formatCurrency(summary.deductionsBreakdown.pf)}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <Typography sx={{ fontWeight: 500 }}>ESI:</Typography>
                <Typography sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {formatCurrency(summary.deductionsBreakdown.esi)}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(245, 158, 11, 0.2)'
              }}>
                <Typography sx={{ fontWeight: 500 }}>Professional Tax:</Typography>
                <Typography sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {formatCurrency(summary.deductionsBreakdown.pt)}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(139, 92, 246, 0.2)'
              }}>
                <Typography sx={{ fontWeight: 500 }}>TDS:</Typography>
                <Typography sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {formatCurrency(summary.deductionsBreakdown.tds)}
                </Typography>
              </Box>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(75, 85, 99, 0.1) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(107, 114, 128, 0.2)'
              }}>
                <Typography sx={{ fontWeight: 500 }}>Other:</Typography>
                <Typography sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {formatCurrency(summary.deductionsBreakdown.other)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PayrollSummary;